import { Loading } from "@/components";
import Modal from "@/components/common/Modal";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getIsLoading, setIsApiLoading, setIsLoading } from "@/store/appSlice";
import API from "@/utils/api";
import { yupResolver } from "@hookform/resolvers/yup";
import DT from "datatables.net-dt";
import DataTable from "datatables.net-react";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";

DataTable.use(DT);

type DictationCategory = {
  id: string;
  name: string;
  courseId: string;
  courseName?: string;
  displayOrder: number;
  chapters?: string[];
};

type DictationCourse = {
  id: string;
  name: string;
};

const categorySchema = yup.object().shape({
  name: yup.string().required("カテゴリ名が必須です。"),
  courseId: yup.string().required("コースが必須です。"),
  displayOrder: yup.number().required("表示順が必須です。").min(1),
  chapters: yup.string(),
});

const DictationCategories = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getIsLoading);
  const [categories, setCategories] = useState<DictationCategory[]>([]);
  const [courses, setCourses] = useState<DictationCourse[]>([]);
  const [activeCategory, setActiveCategory] = useState<DictationCategory | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(categorySchema),
  });

  const fetchCategories = async () => {
    dispatch(setIsLoading(true));
    try {
      const response = await API.get("dictation-categories");
      const { data } = response.data;
      setCategories(data);
      // Make categories available globally for event handlers
      (window as any).categories = data;
      (window as any).openEditModal = openEditModal;
      (window as any).handleDeleteCategory = handleDeleteCategory;
    } catch (err) {
      console.error(err);
      toast.error("カテゴリの取得に失敗しました。");
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await API.get("dictation-courses");
      const { data } = response.data;
      setCourses(data);
    } catch (err) {
      console.error(err);
      toast.error("コースの取得に失敗しました。");
    }
  };

  const handleCreateCategory = async (data: any) => {
    try {
      dispatch(setIsApiLoading(true));
      
      // Convert chapters string to array
      const categoryData = {
        ...data,
        chapters: data.chapters ? data.chapters.split(',').map((c: string) => c.trim()) : []
      };
      
      await API.post("dictation-category", categoryData);
      toast.success("カテゴリが作成されました。");
      fetchCategories();
      reset();
      document.querySelector<HTMLButtonElement>("#modalCategory .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "カテゴリの作成に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleUpdateCategory = async (data: any) => {
    if (!activeCategory) return;
    
    try {
      dispatch(setIsApiLoading(true));
      
      // Convert chapters string to array
      const categoryData = {
        ...data,
        chapters: data.chapters ? data.chapters.split(',').map((c: string) => c.trim()) : []
      };
      
      await API.put(`dictation-category/${activeCategory.id}`, categoryData);
      toast.success("カテゴリが更新されました。");
      fetchCategories();
      reset();
      document.querySelector<HTMLButtonElement>("#modalCategory .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "カテゴリの更新に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleDeleteCategory = async (category: DictationCategory) => {
    if (!window.confirm(`「${category.name}」を削除しますか？`)) return;
    
    try {
      dispatch(setIsApiLoading(true));
      await API.delete(`dictation-category/${category.id}`);
      toast.success("カテゴリが削除されました。");
      fetchCategories();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "カテゴリの削除に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setActiveCategory(null);
    reset({
      name: "",
      courseId: "",
      displayOrder: categories.length + 1,
      chapters: "",
    });
  };

  const openEditModal = (category: DictationCategory) => {
    setIsEditMode(true);
    setActiveCategory(category);
    reset({
      ...category,
      chapters: category.chapters?.join(', ') || "",
    });
  };

  useEffect(() => {
    fetchCategories();
    fetchCourses();
  }, []);

  return (
    <>
      <Helmet>
        <title>ディクテーションカテゴリ管理</title>
      </Helmet>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="card-title">ディクテーションカテゴリ管理</h4>
                <div className="d-flex gap-2">
                  <a href="/content" className="btn btn-outline-secondary">
                    <i className="fas fa-arrow-left me-2"></i>
                    コンテンツ管理に戻る
                  </a>
                  <button
                    className="btn btn-primary"
                    data-bs-toggle="modal"
                    data-bs-target="#modalCategory"
                    onClick={openCreateModal}
                  >
                    <i className="fas fa-plus me-2"></i>
                    新規作成
                  </button>
                </div>
              </div>
              <div className="card-body">
                <Loading />
                {!loading && (
                  <div className="table-responsive">
                    <DataTable
                      data={categories}
                      className="display"
                      options={{
                        autoWidth: true,
                        columnDefs: [
                          { targets: "_all", className: "text-start" },
                        ],
                        stripeClasses: [],
                        order: [[2, "asc"]], // Sort by displayOrder
                        searching: true,
                        destroy: true,
                        info: true,
                        paging: true,
                        language: {
                          lengthMenu: "レコード数&nbsp;_MENU_",
                          emptyTable: "データがありません。",
                          search: "検索:",
                          paginate: {
                            first: "最初",
                            last: "最後",
                            next: "次へ",
                            previous: "前へ",
                          },
                        },
                      }}
                      columns={[
                        {
                          title: "カテゴリ名",
                          data: "name",
                          className: "text-start",
                          orderable: true,
                        },
                        {
                          title: "コース",
                          data: "courseName",
                          className: "text-start",
                          orderable: true,
                        },
                        {
                          title: "表示順",
                          data: "displayOrder",
                          className: "text-center",
                          orderable: true,
                        },
                        {
                          title: "チャプター",
                          data: "chapters",
                          className: "text-start",
                          orderable: false,
                          render: (data) => {
                            if (!data || !Array.isArray(data)) return "-";
                            return data.join(", ");
                          },
                        },
                        {
                          title: "操作",
                          data: null,
                          orderable: false,
                          className: "text-center",
                          render: (data, type, row) => {
                            return `
                              <div class="d-flex justify-content-center gap-2">
                                <button class="btn btn-sm btn-outline-primary edit-btn" data-id="${row.id}">
                                  <i class="fas fa-edit"></i> 編集
                                </button>
                                <button class="btn btn-sm btn-outline-danger delete-btn" data-id="${row.id}">
                                  <i class="fas fa-trash"></i> 削除
                                </button>
                              </div>
                            `;
                          },
                        },
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Modal
          id="modalCategory"
          title={isEditMode ? "カテゴリ編集" : "カテゴリ作成"}
        >
          <Form onSubmit={handleSubmit(isEditMode ? handleUpdateCategory : handleCreateCategory)}>
            <Form.Group className="mb-3">
              <Form.Label>カテゴリ名 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="カテゴリ名を入力"
                isInvalid={!!errors.name}
                {...register("name")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>コース <span className="text-danger">*</span></Form.Label>
              <Form.Select
                isInvalid={!!errors.courseId}
                {...register("courseId")}
              >
                <option value="">コースを選択</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.courseId?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>表示順 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                min="1"
                isInvalid={!!errors.displayOrder}
                {...register("displayOrder")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.displayOrder?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>チャプター</Form.Label>
              <Form.Control
                type="text"
                placeholder="チャプター名をカンマ区切りで入力（例: Chapter 1, Chapter 2）"
                {...register("chapters")}
              />
              <Form.Text className="text-muted">
                複数のチャプターがある場合は、カンマ（,）で区切って入力してください。
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                キャンセル
              </button>
              <button type="submit" className="btn btn-primary">
                {isEditMode ? "更新" : "作成"}
              </button>
            </div>
          </Form>
        </Modal>
      </div>
    </>
  );
};

// Add event listeners for edit and delete buttons
document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;
  
  if (target.closest(".edit-btn")) {
    const id = target.closest(".edit-btn")?.getAttribute("data-id");
    const category = (window as any).categories?.find((c: DictationCategory) => c.id === id);
    if (category) {
      (window as any).openEditModal?.(category);
    }
  }
  
  if (target.closest(".delete-btn")) {
    const id = target.closest(".delete-btn")?.getAttribute("data-id");
    const category = (window as any).categories?.find((c: DictationCategory) => c.id === id);
    if (category) {
      (window as any).handleDeleteCategory?.(category);
    }
  }
});

export default DictationCategories;